/**
 * storageQuota — localStorage quota management for the chat surface
 * (2026-08-22, Miles live incident: FIRESTORE INTERNAL ASSERTION b815 —
 * "QuotaExceededError: Failed to execute 'setItem' on 'Storage'" → full-page
 * crash boundary).
 *
 * Failure chain this module breaks
 * ──────────────────────────────────────────────────────────────────────
 * Per-thread transcript caches accumulate without bound (hundreds of
 * threads × workspaces). Once the origin's localStorage quota fills:
 *   • the Firestore web SDK's OWN setItem (watch-target metadata,
 *     'firestore_targets_*') throws → internal assertion b815 → the whole
 *     surface crashes to the error boundary (Miles's screenshot);
 *   • every app write silently fails (setToLocalStorage swallows), so
 *     reads return a FROZEN STALE state forever — stuck "Queued" behind a
 *     dead turn, wedge-detection reading a corpse as live, transcripts
 *     that "go missing" on reload.
 *
 * Strategy
 * ──────────────────────────────────────────────────────────────────────
 * The per-thread transcript caches (prest0_thread_msgs__*) are CACHES:
 * the server store is the durable copy (threadPersist additionally spills
 * to IndexedDB on quota). They are therefore the one key class that is
 * always safe to evict. This module:
 *   1. reclaimThreadCacheBytes(bytesNeeded, protect): evicts cold thread
 *      caches — largest-first among stale ones (newest-message timestamp
 *      older than RECENT_MS), then largest-first among the rest — never
 *      touching protected keys or any non-thread-cache key.
 *   2. quotaSafeSetItem(key, json): setItem with reclaim-and-retry on
 *      QuotaExceededError; latches a one-shot degraded warning when even
 *      eviction cannot make room. Wired as the chokepoint under
 *      utils.setToLocalStorage, so every app write self-heals.
 *   3. sweepStorageAtBoot(protect): proactive headroom — when estimated
 *      usage exceeds SOFT_LIMIT, evict down to TARGET. This is the only
 *      protection available to the Firestore SDK's own raw setItem calls,
 *      which cannot be wrapped.
 *
 * No imports from utils/threadPersist (they sit above this module — the
 * chokepoint wiring would cycle). The key prefix is duplicated from
 * threadPersist.THREAD_MSG_KEY_PREFIX and pinned equal by test. The imports
 * are two leaves that import nothing: threadCacheTier, where a thread write
 * this module finally cannot land is HELD, so the rest of this tab never
 * reads the frozen copy as current (2026-09-23, the newest-message flicker);
 * and utils/storageHeadroom, which owns the eviction order (2026-09-28): the
 * same rules make room here, before Firestore starts, and when Firestore's own
 * write hits the quota, and none of them parses a transcript to rank it.
 */

import { holdRefusedWrite, releaseHeldCopy } from './threadCacheTier';
import { COLD_MS, evictThreadCaches } from '../../utils/storageHeadroom';

/** Lockstep duplicate of threadPersist.THREAD_MSG_KEY_PREFIX (test-pinned). */
export const THREAD_CACHE_KEY_PREFIX = 'prest0_thread_msgs__';

/** Usage above this at boot triggers the proactive sweep (bytes, UTF-16). */
export const SOFT_LIMIT_BYTES = 3_500_000;
/** The proactive sweep evicts down to roughly this usage. */
export const TARGET_BYTES = 2_500_000;
/** Caches whose newest message is younger than this are evicted last. */
export const RECENT_MS = COLD_MS;
/** Emergency reclaim frees at least this much beyond the failed write. */
const MIN_RECLAIM_BYTES = 262_144;

let degradedWarned = false;

function storageOrNull(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function isQuotaExceededError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; code?: number };
  return (
    e.name === 'QuotaExceededError'
    || e.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || e.code === 22
    || e.code === 1014
  );
}

/** Rough origin usage in bytes (UTF-16: 2 bytes per code unit, key + value). */
export function estimateLocalStorageBytes(): number {
  const ls = storageOrNull();
  if (!ls) return 0;
  let total = 0;
  try {
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i);
      if (key === null) continue;
      const value = ls.getItem(key);
      total += (key.length + (value ? value.length : 0)) * 2;
    }
  } catch {
    /* mid-iteration storage failure — return what was counted */
  }
  return total;
}

/**
 * Evict cold per-thread caches until `bytesNeeded` is freed (or candidates
 * run out), in utils/storageHeadroom's order: another person's caches on this
 * device, then caches whose newest message is older than RECENT_MS (largest
 * first), then the rest (least recently active first). The open thread's
 * cache and protected keys survive; no other key class is ever touched.
 * Returns the bytes freed.
 */
export function reclaimThreadCacheBytes(bytesNeeded: number, protect?: Iterable<string>): number {
  const ls = storageOrNull();
  if (!ls) return 0;
  const { evictedKeys, evictedChars } = evictThreadCaches(ls, Math.ceil(bytesNeeded / 2), { protect });
  const freed = evictedChars * 2;
  if (evictedKeys > 0) {
    console.warn('[storageQuota] evicted cold thread caches (server store is the durable copy)', {
      count: evictedKeys,
      freedBytes: freed,
    });
  }
  return freed;
}

/**
 * setItem with reclaim-and-retry on quota exhaustion. Returns true when the
 * value is stored. A false return means the bin is full even after evicting
 * every cold cache — logged once (latched) so the console shows the degraded
 * state without a write-storm.
 */
export function quotaSafeSetItem(key: string, json: string, protect?: Iterable<string>): boolean {
  const ls = storageOrNull();
  if (!ls) return false;
  try {
    ls.setItem(key, json);
    degradedWarned = false;
    releaseHeldCopy(key);
    return true;
  } catch (err) {
    if (!isQuotaExceededError(err)) return false;
  }
  const needed = Math.max((key.length + json.length) * 2 * 4, MIN_RECLAIM_BYTES);
  reclaimThreadCacheBytes(needed, new Set([key, ...(protect ?? [])]));
  try {
    ls.setItem(key, json);
    degradedWarned = false;
    releaseHeldCopy(key);
    return true;
  } catch (retryErr) {
    // The bin is full even after eviction. For a thread cache the disk copy is
    // now FROZEN behind what this tab holds — keep this tab's copy where every
    // read of the key will find it (threadCacheTier), instead of letting the
    // frozen one read as current.
    if (key.startsWith(THREAD_CACHE_KEY_PREFIX)) holdRefusedWrite(key, json);
    if (!degradedWarned) {
      degradedWarned = true;
      console.error('[storageQuota] localStorage still full after eviction — writes are degraded', {
        key,
        valueBytes: json.length * 2,
        error: retryErr instanceof Error ? retryErr.message : String(retryErr),
      });
    }
    return false;
  }
}

/** The in-session sweep runs at most this often. */
export const SESSION_SWEEP_EVERY_MS = 60_000;
let lastSessionSweepAt = -Infinity;

/**
 * The same sweep DURING a session (2026-09-28, Malaika's freezes): the boot
 * sweep keeps the bin under SOFT_LIMIT when the chat loads, but a long session
 * — thread after thread opened and saved, long replies — grew it back past
 * where the Firestore SDK's own writes fail. After a thread save lands, at most
 * once a minute, usage is measured (a pass over the keys, no transcript parsed)
 * and cold thread caches are evicted back to TARGET, never the one just saved
 * or the open thread.
 */
export function sweepStorageDuringSession(protect?: Iterable<string>, now: number = Date.now()): number {
  if (now - lastSessionSweepAt < SESSION_SWEEP_EVERY_MS) return 0;
  lastSessionSweepAt = now;
  const used = estimateLocalStorageBytes();
  if (used <= SOFT_LIMIT_BYTES) return 0;
  const freed = reclaimThreadCacheBytes(used - TARGET_BYTES, protect);
  console.warn('[storageQuota] session sweep ran', { usedBytes: used, freedBytes: freed });
  return freed;
}

/** Tests only. */
export function resetSessionSweepForTests(): void {
  lastSessionSweepAt = -Infinity;
}

/**
 * Proactive boot-time sweep: when usage exceeds SOFT_LIMIT, evict cold
 * thread caches down to TARGET. This is the only protection for the
 * Firestore SDK's own raw localStorage writes (the b815 crash class) —
 * emergency reclaim can't intercept those, headroom prevents them.
 * Returns the bytes freed (0 when under the limit).
 */
export function sweepStorageAtBoot(protect?: Iterable<string>): number {
  const used = estimateLocalStorageBytes();
  if (used <= SOFT_LIMIT_BYTES) return 0;
  const freed = reclaimThreadCacheBytes(used - TARGET_BYTES, protect);
  console.warn('[storageQuota] boot sweep ran', { usedBytes: used, freedBytes: freed });
  return freed;
}
